import React, { lazy, Suspense, useMemo, useState, useEffect } from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter, Route, Routes } from 'react-router-dom';
import './index.css';
import { ThemeProvider, CssBaseline, CircularProgress, Box } from '@mui/material';
import reportWebVitals from './reportWebVitals';
import { UserProvider } from './providers/UserProvider';
import App from './App';
import { createAppTheme } from './theme';

// Lazy load route components
const Main = lazy(() => import('./routes/Main'));
const User = lazy(() => import('./routes/User'));
const Followers = lazy(() => import('./routes/Followers'));
const Settings = lazy(() => import('./routes/Settings'));

const RouteLoader = () => (
  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
    <CircularProgress color="primary" />
  </Box>
);

const useSystemTheme = () => {
  const [mode, setMode] = useState(() => {
    if (typeof window !== 'undefined' && window.matchMedia) {
      return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
    }
    return 'dark';
  });

  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = (e) => setMode(e.matches ? 'dark' : 'light');

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  return mode;
};

const ThemedApp = () => {
  const mode = useSystemTheme();
  const theme = useMemo(() => createAppTheme(mode), [mode]);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <UserProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<App />}>
              <Route index element={<Suspense fallback={<RouteLoader />}><Main /></Suspense>} />
              <Route path="/user/:userId" element={<Suspense fallback={<RouteLoader />}><User /></Suspense>} />
              <Route path="/followers" element={<Suspense fallback={<RouteLoader />}><Followers /></Suspense>} />
              <Route path="/settings" element={<Suspense fallback={<RouteLoader />}><Settings /></Suspense>} />
            </Route>
          </Routes>
        </BrowserRouter>
      </UserProvider>
    </ThemeProvider>
  );
};

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  <React.StrictMode>
    <ThemedApp />
  </React.StrictMode>,
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
